import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { FileText, Clock, Users, FolderOpen } from 'lucide-react';
import { ProjectService } from '../../services/projectService';
import ContextMenu from '../ContextMenu';
import InlineRenameInput from '../InlineRenameInput';

// Removed mock data - now using real API data

export function ProjectGrid({ searchQuery, activeSection, selectedProject, viewMode = "grid", userInfo, onProjectClick }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    project: null
  });
  
  // Rename state
  const [renamingProject, setRenamingProject] = useState(null);

  // Fetch projects when userInfo changes
  useEffect(() => {
    if (userInfo?.userId) {
      fetchProjects();
    }
  }, [userInfo]);

  const fetchProjects = async () => {
    if (!userInfo?.userId) return;
    
    try {
      setLoading(true);
      setError(null);
      const projectsData = await ProjectService.getUserProjects(userInfo.userId);
      setProjects(projectsData);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  // Context menu handlers
  const handleRightClick = (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      project: project
    });
  };

  const closeContextMenu = () => {
    setContextMenu({
      isOpen: false,
      position: { x: 0, y: 0 },
      project: null
    });
  };

  const handleRename = () => {
    setRenamingProject(contextMenu.project);
    closeContextMenu();
  };

  const handleDelete = async () => {
    const project = contextMenu.project;
    closeContextMenu();
    
    if (window.confirm(`Are you sure you want to delete "${project.name}"? This action cannot be undone.`)) {
      try {
        await ProjectService.deleteProject(project._id);
        setProjects(prev => prev.filter(p => p._id !== project._id));
      } catch (err) {
        console.error('Error deleting project:', err);
        setError(err.message || 'Failed to delete project');
      }
    }
  };

  // Rename handlers
  const handleRenameConfirm = async (newName) => {
    try {
      await ProjectService.renameProject(renamingProject._id, newName);
      setProjects(prev => 
        prev.map(p => 
          p._id === renamingProject._id 
            ? { ...p, name: newName }
            : p
        )
      );
      setRenamingProject(null);
    } catch (err) {
      console.error('Error renaming project:', err);
      throw new Error(err.message || 'Failed to rename project');
    }
  };

  const handleRenameCancel = () => {
    setRenamingProject(null);
  };

  const filteredProjects = projects.filter(
    (project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (project.description && project.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-900/30 text-green-400 border-green-800';
      case 'In Progress':
        return 'bg-blue-900/30 text-blue-400 border-blue-800';
      case 'Migrated':
        return 'bg-purple-900/30 text-purple-400 border-purple-800';
      case 'Draft':
        return 'bg-yellow-900/30 text-yellow-400 border-yellow-800';
      case 'Active':
        return 'bg-slate-800 text-slate-300 border-slate-700';
      default:
        return 'bg-slate-800 text-slate-400 border-slate-700';
    }
  };

  const formatTimeAgo = (date) => {
    if (!date) return 'Unknown';
    
    const now = new Date();
    const past = new Date(date);
    const diffTime = Math.abs(now - past);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      return diffHours <= 1 ? 'Just now' : `${diffHours} hours ago`;
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return `${Math.floor(diffDays / 7)} weeks ago`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">Error loading projects</div>
        <div className="text-slate-400 text-sm">{error}</div>
        <button 
          onClick={fetchProjects}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  // No projects state
  if (filteredProjects.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        {searchQuery 
          ? `No projects found matching "${searchQuery}"`
          : 'No projects yet. Create a new project to get started!'
        }
      </div>
    );
  }

  if (viewMode === "list") {
    return (
      <div className="space-y-4">
        {filteredProjects.map((project) => (
          <Card
            key={project._id}
            className="group drop-shadow-lg hover:shadow-md transition-all duration-100 cursor-pointer
            border-2 border-[#494646]/50 bg-[#2a282a] hover:border-slate-600"
            onClick={() => onProjectClick && onProjectClick(project)}
            onContextMenu={(e) => handleRightClick(e, project)}
          >
            <CardContent className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0">
                    <div className="w-full h-full flex items-center justify-center pt-2">
                      {project.isLegacyProject ? (
                        <FolderOpen className="h-8 w-8 text-purple-400" />
                      ) : (
                        <FolderOpen className="h-8 w-8 text-blue-400" />
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 pt-2">
                      {renamingProject && renamingProject._id === project._id ? (
                        <InlineRenameInput
                          initialValue={project.name}
                          onConfirm={handleRenameConfirm}
                          onCancel={handleRenameCancel}
                          placeholder="Project name..."
                          className="flex-1"
                        />
                      ) : (
                        <h3 className="font-semibold text-slate-100 text-lg">{project.name}</h3>
                      )}
                      {/* <Badge variant="outline" className={getStatusColor(project.status || 'Active')}>
                        {project.status || 'Active'}
                      </Badge>
                      {project.isLegacyProject && (
                        <Badge variant="outline" className="bg-orange-900/30 text-orange-400 border-orange-800">
                          Legacy
                        </Badge>
                      )} */}
                    </div>
                    <p className="text-sm text-slate-400 mt-1">{project.description || 'No description'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="pt-2 flex items-center gap-6 text-sm text-slate-400">
                    <div className="flex items-center gap-1">
                      <FolderOpen className="h-4 w-4" />
                      <span>{project.collectionsCount || 0} collections</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <FileText className="h-4 w-4" />
                      <span>{project.filesCount || project.totalFiles || 0} files</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>{(project.collaborators && project.collaborators.length) || 0}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{formatTimeAgo(project.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {/* Context Menu */}
        <ContextMenu
          isOpen={contextMenu.isOpen}
          position={contextMenu.position}
          onClose={closeContextMenu}
          onRename={handleRename}
          onDelete={handleDelete}
          itemName={contextMenu.project?.name || ''}
          itemType="project"
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {filteredProjects.map((project) => (
        <Card
          key={project._id}
          className="group drop-shadow-2xl hover:shadow-lg transition-all duration-200 cursor-pointer 
                    border-2 border-[#494646] hover:border-[#fef3ec]/50
                    bg-[#2a282a]"
          onClick={() => onProjectClick && onProjectClick(project)}
          onContextMenu={(e) => handleRightClick(e, project)}
        >
          <CardContent className="p-0">
            {/* Header */}
            <div className="flex items-center justify-center relative py-6"> 
              <div className="absolute inset-0 bg-slate-800/5 rounded-t-lg" />
              {project.isLegacyProject ? (
                <FolderOpen className="h-12 w-12 text-purple-400" />
              ) : (
                <FolderOpen className="h-12 w-12 text-blue-400" />
              )}

              {/* Status badge */}
              {/* <div className="absolute top-2 right-2">
                <Badge
                  variant="outline"
                  className={`${getStatusColor(project.status || 'Active')} text-xs px-1 py-0`}
                >
                  {project.status === 'active' ? 'Active' : project.status || 'Active'}
                </Badge>
              </div> */}
            </div>

            {/* Body */}
            <div className=""> 
              {/* Title */}
              <div className="flex items-start justify-between mb-2">
                {renamingProject && renamingProject._id === project._id ? (
                  <InlineRenameInput
                    initialValue={project.name}
                    onConfirm={handleRenameConfirm}
                    onCancel={handleRenameCancel}
                    placeholder="Project name..."
                    className="flex-1"
                    inputClassName="text-lg"
                  />
                ) : (
                  <h3 className="font-semibold text-slate-100 text-lg line-clamp-1 flex-1 leading-tight text-left">
                    {project.name}
                  </h3>
                )}
                {project.isLegacyProject && (
                  <Badge variant="outline" className="ml-1 bg-orange-900/30 text-orange-400 border-orange-800 text-xs px-1 py-0 flex-shrink-0">
                    Legacy
                  </Badge>
                )}
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 text-xs text-slate-400 mb-2">
                <div className="flex items-center gap-1">
                  <FolderOpen className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{project.collectionsCount || 0}</span>
                </div>
                <div className="flex items-center gap-1">
                  <FileText className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{project.filesCount || project.totalFiles || 0}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{(project.collaborators && project.collaborators.length) || 0}</span>
                </div>
              </div>

              {/* Last modified */}
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Clock className="h-3 w-3" />
                <span className="truncate">{formatTimeAgo(project.updatedAt)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      
      {/* Context Menu */}
      <ContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        onClose={closeContextMenu}
        onRename={handleRename}
        onDelete={handleDelete}
        itemName={contextMenu.project?.name || ''}
        itemType="project"
      />
    </div>
  );
}